import React, { useState } from 'react';
import { Button, Checkbox, Drawer, Input, Segmented } from 'antd';
import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import {
  AiReceptionistActivityCatalogItem,
  AiReceptionistMotifCatalogItem,
  CUSTOM_INSTRUCTION_MAX_LENGTH,
} from '../../../../domain/models/AiReceptionist';

type MotifModes = Record<string, { mode: 'automatic' | 'custom'; customInstruction: string }>;

interface ActivityGroup {
  activity: AiReceptionistActivityCatalogItem;
  motifs: AiReceptionistMotifCatalogItem[];
}

interface ActivityMotifsSectionProps {
  assistantName: string;
  activityGroups: ActivityGroup[];
  alwaysMotifs: AiReceptionistMotifCatalogItem[];
  selectedActivityIds: string[];
  onToggleActivity: (activityId: string) => void;
  motifModes: MotifModes;
  onSetMotifMode: (motifId: string, mode: 'automatic' | 'custom', customInstruction: string) => void;
}

const ActivityMotifsSection: React.FC<ActivityMotifsSectionProps> = ({
  assistantName,
  activityGroups,
  alwaysMotifs,
  selectedActivityIds,
  onToggleActivity,
  motifModes,
  onSetMotifMode,
}) => {
  const [editingMotif, setEditingMotif] = useState<AiReceptionistMotifCatalogItem | null>(null);
  const [draftMode, setDraftMode] = useState<'automatic' | 'custom'>('automatic');
  const [draftInstruction, setDraftInstruction] = useState('');

  const name = assistantName || 'Donna';

  const openMotifEditor = (motif: AiReceptionistMotifCatalogItem) => {
    const current = motifModes[motif.id] ?? { mode: 'automatic' as const, customInstruction: '' };
    setEditingMotif(motif);
    setDraftMode(current.mode);
    setDraftInstruction(current.customInstruction);
  };

  const confirmMotifEditor = () => {
    if (!editingMotif) return;
    onSetMotifMode(editingMotif.id, draftMode, draftInstruction);
    setEditingMotif(null);
  };

  const renderMotifRow = (motif: AiReceptionistMotifCatalogItem) => {
    const state = motifModes[motif.id] ?? { mode: 'automatic' as const, customInstruction: '' };
    const isCustom = state.mode === 'custom' && !!state.customInstruction.trim();

    return (
      <button
        key={motif.id}
        type="button"
        className="receptionist-ai-detail-page__motif-row"
        onClick={() => openMotifEditor(motif)}
      >
        <span className="receptionist-ai-detail-page__motif-text">
          <span className="receptionist-ai-detail-page__motif-title">{motif.label}</span>
          <span className="receptionist-ai-detail-page__motif-subtitle">{motif.description}</span>
        </span>
        <span
          className={`receptionist-ai-detail-page__motif-pill receptionist-ai-detail-page__motif-pill--${isCustom ? 'custom' : 'automatic'}`}
        >
          {isCustom ? 'Personnalisé' : 'Automatique'}
        </span>
        <RightOutlined className="receptionist-ai-detail-page__motif-chevron" />
      </button>
    );
  };

  return (
    <>
      <p className="receptionist-ai-detail-page__section-subtitle">
        Cochez ce qui s’applique : les motifs d’appel de chaque activité apparaissent dessous, déjà réglés. Ouvrez un
        motif pour le passer en personnalisé.
      </p>

      <div className="receptionist-ai-detail-page__activities">
        {activityGroups.map(({ activity, motifs }) => {
          const selected = selectedActivityIds.includes(activity.id);

          return (
            <div
              key={activity.id}
              className={`receptionist-ai-detail-page__activity-card${selected ? ' receptionist-ai-detail-page__activity-card--selected' : ''}`}
            >
              <Checkbox
                className="receptionist-ai-detail-page__activity-head"
                checked={selected}
                onChange={() => onToggleActivity(activity.id)}
              >
                <span className="receptionist-ai-detail-page__activity-title">{activity.label}</span>
                <span className="receptionist-ai-detail-page__activity-subtitle">{activity.description}</span>
              </Checkbox>

              {selected && motifs.length > 0 && (
                <div className="receptionist-ai-detail-page__motif-list">{motifs.map(renderMotifRow)}</div>
              )}
            </div>
          );
        })}
      </div>

      {alwaysMotifs.length > 0 && (
        <>
          <h4 className="receptionist-ai-detail-page__always-title">Toujours pris en charge</h4>
          <div className="receptionist-ai-detail-page__motif-list">{alwaysMotifs.map(renderMotifRow)}</div>
        </>
      )}

      <Drawer
        title={editingMotif?.label}
        open={!!editingMotif}
        onClose={() => setEditingMotif(null)}
        width={440}
        closable={false}
        extra={<Button type="text" icon={<CloseOutlined />} onClick={() => setEditingMotif(null)} />}
        footer={
          <div className="receptionist-ai-detail-page__drawer-footer">
            <Button onClick={() => setEditingMotif(null)}>Annuler</Button>
            <Button type="primary" onClick={confirmMotifEditor}>
              Enregistrer
            </Button>
          </div>
        }
      >
        {editingMotif && (
          <>
            <p className="receptionist-ai-detail-page__section-subtitle">
              Ce que {name} fait quand quelqu’un appelle pour ça.
            </p>

            <Segmented
              block
              className="receptionist-ai-detail-page__drawer-modes"
              value={draftMode}
              onChange={(value) => setDraftMode(value as 'automatic' | 'custom')}
              options={[
                { label: 'Automatique', value: 'automatic' },
                { label: 'Personnalisé', value: 'custom' },
              ]}
            />

            {draftMode === 'automatic' ? (
              <>
                <div className="receptionist-ai-detail-page__auto-box">
                  <span className="receptionist-ai-detail-page__auto-box-title">
                    Ce que {name} fait en automatique
                  </span>
                  <p className="receptionist-ai-detail-page__auto-box-text">{editingMotif.default_flow}</p>
                </div>
                <p className="receptionist-ai-detail-page__hint">
                  Réglage entretenu par Leedflow. {name} pose ses questions dans le fil de la conversation, sans
                  interrogatoire, puis transmet.
                </p>
              </>
            ) : (
              <>
                <label className="receptionist-ai-detail-page__drawer-label" htmlFor="receptionist-ai-motif-instruction">
                  Ce que {name} doit faire pour ce motif, avec vos mots
                </label>
                <Input.TextArea
                  id="receptionist-ai-motif-instruction"
                  value={draftInstruction}
                  onChange={(e) => setDraftInstruction(e.target.value)}
                  maxLength={CUSTOM_INSTRUCTION_MAX_LENGTH}
                  rows={6}
                />
                <div className="receptionist-ai-detail-page__field-footer">
                  <p className="receptionist-ai-detail-page__hint">
                    Écrivez-lui comme à une nouvelle collègue : « Dis-lui que… », « Demande-lui… ».
                  </p>
                  <span className="receptionist-ai-detail-page__count">
                    {draftInstruction.length} / {CUSTOM_INSTRUCTION_MAX_LENGTH}
                  </span>
                </div>
                <p className="receptionist-ai-detail-page__example">
                  <strong>Exemple</strong> — Demande-lui le secteur et le budget, puis dis-lui que toutes nos annonces
                  sont sur agence-martin.fr et qu’il peut y demander une visite.
                </p>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default ActivityMotifsSection;
